import { useEffect, useRef, useState } from "react";

/**
 * CustomCursor
 *
 * Rewritten to write transforms directly to the DOM from a single rAF loop.
 * The previous version put pointer position in React state, which re-rendered
 * this component (and re-ran framer-motion's spring) on every mousemove —
 * expensive on its own, and actively harmful next to a WebGL canvas that
 * wants the frame budget.
 *
 * Now: two refs, one loop, zero re-renders while moving.
 */
export default function CustomCursor() {
  const ring = useRef(null);
  const dot = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Pointer-based, not width-based: a small laptop still has a mouse.
    if (
      window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // target = true pointer position, current = eased follower
    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ringPos = { ...target };
    const dotPos = { ...target };
    const state = { hover: 0, down: 0 };
    let raf;

    const onMove = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
    };
    const onDown = () => (state.down = 1);
    const onUp = () => (state.down = 0);

    const onOver = (e) => {
      const t = e.target;
      state.hover =
        t.closest?.(
          "a, button, [role='button'], summary, input, textarea, [data-cursor='hover']"
        )
          ? 1
          : 0;
    };

    // Hide the cursor entirely when the pointer leaves the window,
    // otherwise it sticks to the last known edge position.
    const onLeave = () => {
      if (ring.current) ring.current.style.opacity = "0";
      if (dot.current) dot.current.style.opacity = "0";
    };
    const onEnter = () => {
      if (ring.current) ring.current.style.opacity = "1";
      if (dot.current) dot.current.style.opacity = "1";
    };

    const loop = () => {
      // two different easing rates give the ring its trailing lag
      ringPos.x += (target.x - ringPos.x) * 0.16;
      ringPos.y += (target.y - ringPos.y) * 0.16;
      dotPos.x += (target.x - dotPos.x) * 0.42;
      dotPos.y += (target.y - dotPos.y) * 0.42;

      const ringScale = state.hover ? 1.7 : state.down ? 0.85 : 1;
      const dotScale = state.down ? 0.5 : state.hover ? 0.4 : 1;

      if (ring.current) {
        ring.current.style.transform =
          `translate3d(${ringPos.x}px, ${ringPos.y}px, 0) translate(-50%, -50%) scale(${ringScale})`;
        ring.current.style.borderColor = state.hover
          ? "rgba(52,211,153,0.85)"
          : "rgba(255,255,255,0.35)";
        ring.current.style.backgroundColor = state.hover
          ? "rgba(16,185,129,0.10)"
          : "transparent";
      }
      if (dot.current) {
        dot.current.style.transform =
          `translate3d(${dotPos.x}px, ${dotPos.y}px, 0) translate(-50%, -50%) scale(${dotScale})`;
      }

      raf = requestAnimationFrame(loop);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", onDown, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={ring}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[9999] hidden h-9 w-9 rounded-full border md:block"
        style={{
          willChange: "transform",
          transition: "border-color 0.3s, background-color 0.3s, opacity 0.3s",
        }}
      />
      <div
        ref={dot}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[9999] hidden h-1.5 w-1.5 rounded-full bg-brand-300 md:block"
        style={{ willChange: "transform", transition: "opacity 0.3s" }}
      />
    </>
  );
}
